import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import NewAnalyticsApi from 'api/new-analytics';
import type { PageHealth } from 'api/new-analytics/types';
import { queryKey } from 'constants/queryKey';
import { ApiClientError } from 'api/client';

const STALE_TIME_MS = 5 * 60 * 1000;

/**
 * Read-only view of profile health. React Query dedupes by key, so this is
 * safe to call from anywhere — it does not need to be threaded through a
 * context, and putting it there only made every health refetch re-render every
 * analytics consumer.
 *
 * Deliberately side-effect free: the error toast is owned once by
 * `AnalyticsHealthDriver`, otherwise one failed fetch would raise one toast per
 * call site.
 */
export function useAnalyticsHealthQuery() {
  const queryClient = useQueryClient();

  const healthQuery = useQuery<PageHealth[], ApiClientError>(
    queryKey.analyticsHealth(),
    () => NewAnalyticsApi.getHealth(),
    { staleTime: STALE_TIME_MS },
  );

  const healthByPageId = useMemo(() => {
    const map: Record<number, PageHealth> = {};
    for (const row of healthQuery.data ?? []) {
      map[row.page_id] = row;
    }
    return map;
  }, [healthQuery.data]);

  const getPageHealth = useCallback(
    (pageId: number): PageHealth | undefined => healthByPageId[pageId],
    [healthByPageId],
  );

  const refetchPageHealth = useCallback(
    async (pageId: number): Promise<PageHealth | undefined> => {
      await queryClient.refetchQueries(queryKey.analyticsHealth());
      const rows = queryClient.getQueryData<PageHealth[]>(
        queryKey.analyticsHealth(),
      );
      return rows?.find((row) => row.page_id === pageId);
    },
    [queryClient],
  );

  return {
    healthByPageId,
    isLoading: healthQuery.isLoading,
    isError: healthQuery.isError,
    errorUpdatedAt: healthQuery.errorUpdatedAt,
    error: healthQuery.error,
    getPageHealth,
    refetchPageHealth,
  };
}
